import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {useReducedMotion} from 'framer-motion'
import ModuleContainer from '../../components/module-container'
import {Grid, GridItem, Box, Flex, useSlotRecipe} from '@chakra-ui/react3'
import Asset from '../../components/cms/asset'
import Fade from '../../components/cms/fade'
import Heading from '../../components/cms/heading'
import Copy from '../../components/cms/copy'
import CTA from '../../components/cms/cta'
import PriceButtonGroup from '../../components/cms/price-button-group'
import ButtonGroup from '../../components/cms/button-group'
import {hasValidString} from '../../utils/utils'
import {collageRecipe} from './index.recipe'
import useIntersectionObserver from '../../../hooks/use-intersection-observer'

/**
 * Collage module component that displays a gallery of images in a responsive grid layout
 * with staggered fade-in animations.
 *
 * The component uses IntersectionObserver to trigger animations when the grid becomes visible
 * in the viewport (50% threshold). Images are arranged in a collage pattern with different
 * aspect ratios (portrait, landscape, square, standard) and animate with configurable delays.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.moduleId] - Unique identifier for the module
 * @param {string} [props.moduleType] - Type of the module
 * @param {string} [props.pageId] - ID of the page containing this module
 * @param {number} [props.headingLevel] - Heading level for accessibility (1-6)
 * @param {Object} [props.heading] - Heading configuration object
 * @param {Object} [props.copy] - Copy/text content configuration
 * @param {string} [props.anchor] - Anchor ID for deep linking
 * @param {'default'|'subtle'|'strong'|'inverse'} [props.background] - Background variant
 * @param {'default'|'reduced'|'min'|'min-top'|'min-bottom'} [props.spacing] - Spacing variant
 * @param {Object} [props.images] - Image collections by aspect ratio
 * @param {Array} [props.images.portrait] - Portrait-oriented images (2 required)
 * @param {Array} [props.images.landscape] - Landscape-oriented images (2 required)
 * @param {Array} [props.images.square] - Square images (3 required)
 * @param {Array} [props.images.standard] - Standard aspect ratio images (1 required)
 * @param {Object} [props.firstCta] - Primary call-to-action configuration
 * @param {Object} [props.priceProduct] - Product pricing information
 * @param {Object} [props.secondCta] - Secondary call-to-action configuration
 * @param {React.Ref} ref - Forwarded ref to the module container
 * @returns {React.ReactElement} The rendered Collage component
 *
 * @example
 * <Collage
 *   moduleId="collage-1"
 *   heading={{text: "Our Gallery"}}
 *   images={{
 *     portrait: [image1, image2],
 *     landscape: [image3, image4],
 *     square: [image5, image6, image7],
 *     standard: [image8]
 *   }}
 *   firstCta={{label: "Shop Now", href: "/shop"}}
 * />
 */
const Collage = React.forwardRef(function Collage(
    {
        moduleId,
        moduleType,
        pageId,
        headingLevel,
        heading,
        copy,
        anchor,
        background,
        images,
        firstCta,
        priceProduct,
        secondCta
    },
    ref
) {
    const reducedMotionEnabled = useReducedMotion()

    const [gridRef, isIntersecting] = useIntersectionObserver({
        threshold: 0.5,
        initialInView: reducedMotionEnabled
    })

    const [isVisible, setIsVisible] = React.useState(reducedMotionEnabled)
    const [triggerFirstRow, setTriggerFirstRow] = React.useState(false)
    const [triggerSecondRow, setTriggerSecondRow] = React.useState(false)
    const [triggerThirdRow, setTriggerThirdRow] = React.useState(false)

    React.useEffect(() => {
        if (reducedMotionEnabled) {
            setIsVisible(true)
            return
        }

        if (isIntersecting) {
            let isMounted = true
            // waiting for fade animation
            setTriggerFirstRow(true)
            const secondRowTimer = setTimeout(() => {
                if (isMounted) {
                    setTriggerSecondRow(true)
                }
            }, 100)
            const thirdRowTimer = setTimeout(() => {
                if (isMounted) {
                    setTriggerThirdRow(true)
                }
            }, 200)
            const timer = setTimeout(() => {
                if (isMounted) {
                    setIsVisible(true)
                }
            }, 2000) // 2 seconds = Fade duration + delay

            return () => {
                isMounted = false
                clearTimeout(secondRowTimer)
                clearTimeout(thirdRowTimer)
                clearTimeout(timer)
            }
        }
    }, [isIntersecting, reducedMotionEnabled])

    const recipe = useSlotRecipe({recipe: collageRecipe})
    const styles = useMemo(() => recipe({isVisible}), [isVisible, recipe])

    const hasFirstCtaLabel = hasValidString(firstCta?.label)
    const hasSecondCtaLabel = hasValidString(secondCta?.label)
    const hasPrice = priceProduct?.value != null

    return (
        <ModuleContainer
            moduleId={moduleId}
            moduleType={moduleType}
            pageId={pageId}
            anchor={anchor}
            background={background}
            spacing="none"
            ref={ref}
            {...styles.moduleContainer}
            height={{base: '50rem', xs: '64rem', s: '74.75rem'}}
            modulePadding="collage"
        >
            <Box {...styles.textBox}>
                <Heading
                    alignment="center"
                    {...heading}
                    headingLevel={headingLevel}
                    {...styles.textBoxElement}
                />

                <Copy alignment="center" paddingTop="spacing-16" {...copy} />

                <ButtonGroup paddingTop="spacing-32" justifyContent="center">
                    {hasFirstCtaLabel && !hasPrice && <CTA {...firstCta} />}

                    {hasFirstCtaLabel && hasPrice && !hasSecondCtaLabel && (
                        <PriceButtonGroup price={priceProduct} cta={firstCta} />
                    )}

                    {hasSecondCtaLabel && <CTA {...secondCta} />}
                </ButtonGroup>
            </Box>

            <Flex justifyContent="center">
                <Box {...styles.gridContainer}>
                    <Grid ref={gridRef} {...styles.grid}>
                        {/* top left */}
                        <GridItem css={[styles.topLeft]}>
                            <Fade delay={0} translateY="24px" trigger={triggerFirstRow}>
                                <Box css={[styles.topLeftBox, styles.imageBox]}>
                                    <Asset
                                        asset={images?.portrait[0]}
                                        objectFit="cover"
                                        widths={{base: '81px', s: '237px'}}
                                    />
                                </Box>
                            </Fade>
                        </GridItem>
                        {/* top right */}
                        <GridItem css={[styles.topRight]}>
                            <Fade delay={0.2} translateY="24px" trigger={triggerFirstRow}>
                                <Box {...styles.topRightBox} {...styles.imageBox}>
                                    <Asset
                                        asset={images?.landscape[0]}
                                        objectFit="cover"
                                        widths={{base: '148px', s: '432px'}}
                                    />
                                </Box>
                            </Fade>
                        </GridItem>

                        <GridItem {...styles.leftColumn}>
                            <Flex flexDirection="column" gap={4} alignItems="end">
                                {/* left center */}
                                <Fade delay={0.2} translateY="24px" trigger={triggerSecondRow}>
                                    <Box
                                        css={[
                                            styles.leftCenter,
                                            styles.leftCenterBox,
                                            styles.imageBox
                                        ]}
                                    >
                                        <Asset
                                            asset={images?.landscape[1]}
                                            objectFit="cover"
                                            widths={{base: '140px', s: '410px'}}
                                        />
                                    </Box>
                                </Fade>
                                {/* bottom left */}
                                <Box css={[styles.bottomLeft, styles.bottomLeftBox]}>
                                    <Fade delay={0} translateY="24px" trigger={triggerThirdRow}>
                                        <Box css={[styles.imageBox]}>
                                            <Asset
                                                asset={images?.square[1]}
                                                objectFit="cover"
                                                widths={{base: '92px', s: '270px'}}
                                            />
                                        </Box>
                                    </Fade>
                                </Box>
                            </Flex>
                        </GridItem>
                        <GridItem {...styles.rightColumn}>
                            <Flex gap={4} alignItems="start">
                                {/* center center */}
                                <Flex flexDirection="column" gap={4}>
                                    <Box css={[styles.centerCenter]}>
                                        <Fade
                                            delay={0.2}
                                            translateY="24px"
                                            trigger={triggerSecondRow}
                                        >
                                            <Box {...styles.centerCenterBox} {...styles.imageBox}>
                                                <Asset
                                                    asset={images?.portrait[1]}
                                                    objectFit="cover"
                                                    widths={{base: '103px', s: '302px'}}
                                                />
                                            </Box>
                                        </Fade>
                                    </Box>
                                    {/*bottom center*/}
                                    <Box css={[styles.bottomCenter, styles.bottomCenterBox]}>
                                        <Fade
                                            delay={0.2}
                                            translateY="24px"
                                            trigger={triggerThirdRow}
                                        >
                                            <Box css={[styles.imageBox]}>
                                                <Asset
                                                    asset={images?.standard[0]}
                                                    objectFit="cover"
                                                    widths={{base: '103px', s: '302px'}}
                                                />
                                            </Box>
                                        </Fade>
                                    </Box>
                                </Flex>
                                <Flex flexDirection="column" gap={4}>
                                    {/* center right */}
                                    <Box css={[styles.centerRight, styles.centerRightBox]}>
                                        <Fade
                                            delay={0.4}
                                            translateY="24px"
                                            trigger={triggerSecondRow}
                                        >
                                            <Box css={[styles.imageBox]}>
                                                <Asset
                                                    asset={images?.square[0]}
                                                    objectFit="cover"
                                                    widths={{base: '100px', s: '289px'}}
                                                />
                                            </Box>
                                        </Fade>
                                    </Box>
                                    {/* bottom right */}
                                    <Box css={[styles.bottomRight, styles.bottomRightBox]}>
                                        <Fade
                                            delay={0.4}
                                            translateY="24px"
                                            trigger={triggerThirdRow}
                                        >
                                            <Box css={[styles.imageBox]}>
                                                <Asset
                                                    asset={images?.square[2]}
                                                    objectFit="cover"
                                                    widths={{base: '80px', s: '232px'}}
                                                />
                                            </Box>
                                        </Fade>
                                    </Box>
                                </Flex>
                            </Flex>
                        </GridItem>
                    </Grid>
                </Box>
            </Flex>
        </ModuleContainer>
    )
})

if (process.env.NODE_ENV !== 'production') {
    Collage.displayName = 'Collage'
}

Collage.propTypes = {
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string,
    headingLevel: PropTypes.number,
    heading: PropTypes.object,
    copy: PropTypes.object,
    anchor: PropTypes.string,
    background: PropTypes.oneOf(['default', 'subtle', 'strong', 'inverse']),
    images: PropTypes.shape({
        landscape: PropTypes.array,
        square: PropTypes.array,
        standard: PropTypes.array,
        portrait: PropTypes.array
    }),
    firstCta: PropTypes.object,
    priceProduct: PropTypes.object,
    secondCta: PropTypes.object
}

export default Collage
